import toTitleCase from '../toTitleCase';

/**
 * El nombre de la gestora de origen tal y como cabe en una línea de tarjeta: «VidaCaixa», no
 * «VIDACAIXA, S.A. DE SEGUROS Y REASEGUROS».
 *
 * Lo que llega es la razón social del registry de la DGSFP (`pension_plan_registry.gestora_nombre`,
 * vía `transfer.catalog.entityName`): casi siempre en mayúsculas, con la forma jurídica, a veces
 * cortada a 50 caracteres y con varias grafías de la misma gestora («VIDACAIXA, S.A.…» y
 * «VIDA-CAIXA SA DE SEGUROS…»). No hay un nombre comercial guardado en ningún sitio, así que se
 * deduce:
 *
 * 1. Lo que va antes de la primera coma o paréntesis, y antes de «(DE) SEGUROS», «ENTIDAD
 *    GESTORA», «SOCIEDAD…» o «GESTORA DE FONDOS» (el objeto social, no el nombre).
 * 2. Sin forma jurídica (S.A., S.A.U., E.G.F.P., S.G.F.P.).
 * 3. Sin el «Pensiones» / «Vida» del final, que es el ramo, salvo que el nombre se quede colgando
 *    de un conector («Laboral de», «March Gestión de»).
 * 4. Mayúscula solo la inicial, salvo las siglas (`SIGLAS`, o palabras sin vocales como CBNK).
 *
 * Probado con las 65 razones sociales del registry el 23/10/2026: las que la regla no acierta van
 * en `EXCEPCIONES`. Una gestora nueva que salga larga se corta por palabras con «…» (`MAX_LARGO`).
 */

/* Por la clave normalizada: tras los pasos 1-3, en mayúsculas y sin tildes. */
const EXCEPCIONES: Record<string, string> = {
	VIDACAIXA: 'VidaCaixa',
	'VIDA-CAIXA': 'VidaCaixa',
	BBVASEGUROS: 'BBVA Seguros',
	'GESTION DE PREVISION Y PENSIONES': 'Gestión de Previsión y Pensiones',
	'UNION DEL DUERO': 'Unión del Duero',
	'NATIONALE-NEDERLANDEN': 'Nationale-Nederlanden',
	'CAIXA ENGINYERS VIDA-CAJA INGENIEROS': 'Caja de Ingenieros',
	'PREVISION SANITARIA NACIONAL': 'Previsión Sanitaria Nacional',
	'SANTA LUCIA': 'Santa Lucía',
	'LAMUTUA DEL ENGINYERS': 'La Mútua dels Enginyers'
};

const OBJETO_SOCIAL =
	/\s(?:DE\s)?SEGUROS\b|\sENTIDAD GESTORA\b|\sSOCIEDAD\b|\sGESTORA DE FONDOS\b/i;

const FORMA_JURIDICA =
	/\b(?:S\.?\s?A\.?(?:\s?U\.?)?|SAU|E\.?\s?G\.?\s?F\.?\s?P\.?|EGFP|S\.?\s?G\.?\s?F\.?\s?P\.?)(?=[\s,.]|$)/gi;

/* Del más largo al más corto: «VIDA PENSIONES» antes que «PENSIONES». */
const RAMO_FINAL =
	/\s+(?:VIDA Y PENSIONES|VIDA PENSIONES|SEGUROS DE VIDA|PENSIONES|PENSION|VIDA)$/i;

const CONECTORES = new Set(['DE', 'DEL', 'LA', 'LAS', 'LOS', 'EL', 'Y', 'E', 'EN', 'A', 'CON']);

/* Una lista y no «cualquier palabra corta en mayúsculas»: si no, VIDA, CAJA o FIJA lo serían. */
const SIGLAS = new Set(['BBVA', 'AXA', 'RGA', 'CAI', 'BWCI', 'GVC', 'PSN', 'ING']);

const esSigla = (palabra: string) =>
	SIGLAS.has(palabra.toUpperCase()) || /^[B-DF-HJ-NP-TV-Z]{2,5}$/i.test(palabra);

const MAX_LARGO = 33;

const sinTildes = (texto: string) => texto.normalize('NFD').replace(/[̀-ͯ]/g, '');

const colapsar = (texto: string) => texto.replace(/\s+/g, ' ').trim();

/** Corta por la última palabra que cabe, con «…». */
export function recortar(nombre: string): string {
	if (nombre.length <= MAX_LARGO) return nombre;
	const trozo = nombre.slice(0, MAX_LARGO + 1);
	const ultimoEspacio = trozo.lastIndexOf(' ');
	return `${ultimoEspacio > 0 ? trozo.slice(0, ultimoEspacio) : nombre.slice(0, MAX_LARGO)}…`;
}

const quitarRamo = (nombre: string) => {
	const sinRamo = nombre.replace(RAMO_FINAL, '').trim();
	const ultima = sinRamo.slice(sinRamo.lastIndexOf(' ') + 1);
	return CONECTORES.has(ultima.toUpperCase()) ? nombre : sinRamo;
};

export function nombreCortoDeGestora(razonSocial?: string | null): string {
	const primerTrozo = colapsar((razonSocial ?? '').split(/[,(]/)[0]).split(OBJETO_SOCIAL)[0];
	const sinForma = colapsar(primerTrozo.replace(FORMA_JURIDICA, ' '));
	const nombre = quitarRamo(sinForma);

	const excepcion = EXCEPCIONES[sinTildes(nombre).toUpperCase()];
	if (excepcion) return excepcion;

	const palabras = nombre.split(' ');
	const enTitulo = toTitleCase(nombre).split(' ');
	return recortar(palabras.map((p, i) => (esSigla(p) ? p.toUpperCase() : enTitulo[i])).join(' '));
}
